import topicsPathwayArray, { topicProgression } from '../infrastructure/topicsPathwayArray.js';

function getLiveTopicSkills(unit, topic) {
  return topicsPathwayArray.filter(
    (skill) => skill.unit === unit && skill.topic === topic && skill.isLive
  );
}

/**
 * One entry per skillsHome circle (standard skill per group).
 *
 * @param {string} unit
 * @param {string} topic
 */
export function getDisplaySkillsForTopic(unit, topic) {
  const topicSkills = getLiveTopicSkills(unit, topic);
  const grouped = topicSkills.reduce((groups, skill) => {
    if (!groups[skill.skill]) {
      groups[skill.skill] = [];
    }
    groups[skill.skill].push(skill);
    return groups;
  }, {});

  return Object.values(grouped)
    .map((group) => {
      const standardSkill = group.find((skill) => skill.isStandard) || group[0];
      return {
        ...standardSkill,
        skillGroup: group,
        isGrouped: group.length > 1,
      };
    })
    .sort((a, b) => a.level - b.level);
}

/**
 * @param {object} skill — entry from topicsPathwayArray
 */
export function getSkillGroupMembers(skill) {
  return getLiveTopicSkills(skill.unit, skill.topic)
    .filter((entry) => entry.skill === skill.skill)
    .sort((a, b) => a.level - b.level);
}

/**
 * @param {object} completedSkill
 */
export function isLastSubskillInGroup(completedSkill) {
  const members = getSkillGroupMembers(completedSkill);
  if (!members.length) {
    return false;
  }
  return members[members.length - 1].skillId === completedSkill.skillId;
}

/**
 * @param {object} completedSkill
 */
export function isLastDisplaySkillInTopic(completedSkill) {
  const displaySkills = getDisplaySkillsForTopic(completedSkill.unit, completedSkill.topic);
  if (!displaySkills.length) {
    return false;
  }
  const lastDisplay = displaySkills[displaySkills.length - 1];
  return completedSkill.skill === lastDisplay.skill;
}

/**
 * @param {object} completedSkill
 * @returns {object | null}
 */
export function getNextDisplaySkillInTopic(completedSkill) {
  const displaySkills = getDisplaySkillsForTopic(completedSkill.unit, completedSkill.topic);
  const currentIndex = displaySkills.findIndex((skill) => skill.skill === completedSkill.skill);
  if (currentIndex < 0 || currentIndex >= displaySkills.length - 1) {
    return null;
  }
  return displaySkills[currentIndex + 1];
}

/**
 * Builds a pathway URL that scrolls to and highlights a target skill.
 * Grouped skills route to subskillsHome; standalone skills use skillsHome.
 *
 * @param {object} targetSkill — entry from topicsPathwayArray
 * @returns {string | null}
 */
export function buildHighlightedSkillPath(targetSkill) {
  if (!targetSkill?.unit || !targetSkill?.topic || targetSkill.skillId == null) {
    return null;
  }

  const groupMembers = getSkillGroupMembers(targetSkill);

  if (groupMembers.length > 1) {
    return `/subskillsHome/${targetSkill.unit}/${targetSkill.topic}/${targetSkill.skill}?highlight=${targetSkill.skillId}`;
  }

  return `/skillsHome/${targetSkill.unit}/${targetSkill.topic}?highlight=${targetSkill.skillId}`;
}

/**
 * Always routes to skillsHome (topic circle view), even when the target is grouped.
 *
 * @param {object} targetSkill — display/standard skill from topicsPathwayArray
 * @returns {string | null}
 */
export function buildSkillsHomeHighlightPath(targetSkill) {
  if (!targetSkill?.unit || !targetSkill?.topic || targetSkill.skillId == null) {
    return null;
  }

  return `/skillsHome/${targetSkill.unit}/${targetSkill.topic}?highlight=${targetSkill.skillId}`;
}

/**
 * @param {string} unit
 * @param {string} currentTopic
 * @returns {string | null}
 */
export function getNextTopicInUnit(unit, currentTopic) {
  const topics = topicProgression[unit] || [];
  const currentIndex = topics.indexOf(currentTopic);
  if (currentIndex < 0 || currentIndex >= topics.length - 1) {
    return null;
  }
  return topics[currentIndex + 1];
}

/**
 * @param {string} unit
 * @param {string} topic
 * @returns {string | null}
 */
export function buildHighlightedTopicPath(unit, topic) {
  if (!unit || !topic) {
    return null;
  }
  return `/topicsHome/${unit}?highlightTopic=${encodeURIComponent(topic)}`;
}

/**
 * Extracts a pathway highlight skillId from a post-completion navigation path.
 *
 * @param {string|null|undefined} path
 * @returns {number|null}
 */
export function getHighlightedSkillIdFromPath(path) {
  if (!path || typeof path !== 'string') {
    return null;
  }

  const queryIndex = path.indexOf('?');
  if (queryIndex === -1) {
    return null;
  }

  const params = new URLSearchParams(path.slice(queryIndex + 1));
  const highlight = params.get('highlight');
  if (highlight == null || highlight === '') {
    return null;
  }

  const skillId = Number(highlight);
  return Number.isFinite(skillId) ? skillId : null;
}

/**
 * Pathway view for the completed skill (where the user chose the skill).
 * Does not advance to the next skill — use for guest "skip" exits.
 *
 * @param {{ skillId?: string | number, unit?: string, topic?: string }} params
 * @returns {string | null}
 */
export function resolvePathwayReturnNavigation({ skillId, unit, topic }) {
  const completedSkill = topicsPathwayArray.find(
    (skill) => String(skill.skillId) === String(skillId)
  );

  if (!completedSkill) {
    if (unit && topic) {
      return `/skillsHome/${unit}/${topic}`;
    }
    return null;
  }

  const resolvedUnit = completedSkill.unit || unit;
  const resolvedTopic = completedSkill.topic || topic;
  if (!resolvedUnit || !resolvedTopic) {
    return null;
  }

  const groupMembers = getSkillGroupMembers(completedSkill);
  if (groupMembers.length > 1) {
    return `/subskillsHome/${resolvedUnit}/${resolvedTopic}/${completedSkill.skill}`;
  }

  return `/skillsHome/${resolvedUnit}/${resolvedTopic}`;
}

/**
 * Structural next-step navigation after completing a skill.
 * Prefers pathway order over completion API (avoids stale "first incomplete" redirects).
 *
 * @param {{ skillId?: string | number, unit?: string, topic?: string }} params
 * @returns {string | null}
 */
export function resolvePostCompletionNavigation({ skillId, unit, topic }) {
  const completedSkill = topicsPathwayArray.find(
    (skill) => String(skill.skillId) === String(skillId)
  );

  if (!completedSkill) {
    return null;
  }

  const resolvedUnit = completedSkill.unit || unit;
  const resolvedTopic = completedSkill.topic || topic;
  if (!resolvedUnit || !resolvedTopic) {
    return null;
  }

  const groupMembers = getSkillGroupMembers(completedSkill);
  const isGrouped = groupMembers.length > 1;
  const lastInGroup = isLastSubskillInGroup(completedSkill);
  const lastInTopic = isLastDisplaySkillInTopic(completedSkill);

  if (lastInTopic && lastInGroup) {
    const nextTopic = getNextTopicInUnit(resolvedUnit, resolvedTopic);
    if (nextTopic) {
      return buildHighlightedTopicPath(resolvedUnit, nextTopic);
    }
    return `/topicsHome/${resolvedUnit}`;
  }

  if (isGrouped && !lastInGroup) {
    const currentIndex = groupMembers.findIndex(
      (member) => member.skillId === completedSkill.skillId
    );
    const nextSubskill = groupMembers[currentIndex + 1];
    return buildHighlightedSkillPath(nextSubskill);
  }

  const nextDisplaySkill = getNextDisplaySkillInTopic(completedSkill);
  return buildSkillsHomeHighlightPath(nextDisplaySkill);
}
